import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X, Download, Maximize2, Loader2, FileText, AlertTriangle, ExternalLink, RotateCw, ZoomIn, ZoomOut } from 'lucide-react';

/**
 * AttachmentPreviewModal — Phase C of Document Management.
 *
 * In-app side-by-side viewer for scanned documents / PDFs / images.
 * Supports:
 *   - Image preview with zoom + rotate
 *   - PDF preview via <object> tag (browser-native)
 *   - "Pop out" button → opens the same blob in a separate browser
 *     window so the user can drag it to a second monitor without
 *     leaving the CRM tab
 *
 * Usage:
 *   <AttachmentPreviewModal api={api} attachment={att} onClose={...} />
 */
export default function AttachmentPreviewModal({ api, attachment, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const bodyRef = useRef(null);

  // Mouse-wheel zoom on the image preview pane. Bound via native
  // addEventListener with { passive: false } so e.preventDefault()
  // suppresses the default container scroll. React's synthetic
  // onWheel is passive and can't stop the scroll on its own.
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (!attachment) return;
      const m = attachment.mime_type || '';
      if (!m.startsWith('image/')) return;  // only zoom on images
      e.preventDefault();
      const step = e.deltaY < 0 ? 0.125 : -0.125;
      setZoom((z) => Math.max(0.25, Math.min(4, +(z + step).toFixed(3))));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [attachment]);

  useEffect(() => {
    if (!attachment) return;
    let alive = true;
    let url = null;
    setLoading(true);
    setErr(null);
    api.get(`/attachments/${attachment._id}/download`, { responseType: 'blob' })
      .then((res) => {
        if (!alive) return;
        const blob = new Blob([res.data], { type: attachment.mime_type || 'application/octet-stream' });
        url = URL.createObjectURL(blob);
        setBlobUrl(url);
      })
      .catch((e) => alive && setErr(e?.response?.data?.detail || e.message))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [api, attachment]);

  const popOut = useCallback(() => {
    if (!blobUrl) return;
    // Opens the blob URL in a new window — operator can drag the new
    // window to their second monitor for true side-by-side reference.
    window.open(blobUrl, '_blank', 'noopener,noreferrer,width=900,height=1000');
  }, [blobUrl]);

  const download = useCallback(() => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = attachment.original_filename || 'attachment';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [blobUrl, attachment]);

  if (!attachment) return null;

  const mime = attachment.mime_type || '';
  const isImage = mime.startsWith('image/');
  const isPdf = mime === 'application/pdf';
  const isText = mime.startsWith('text/') || mime === 'application/json';

  return (
    <div
      data-testid="attachment-preview-modal"
      className="fixed inset-0 bg-black/70 z-50 flex items-stretch justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-5xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-3 bg-slate-50">
          <FileText size={18} className="text-slate-400 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-slate-800 truncate">{attachment.original_filename}</div>
            <div className="text-[11px] text-slate-500 truncate font-mono">{attachment.rel_path}</div>
          </div>
          {isImage && (
            <>
              <button
                data-testid="preview-zoom-out"
                onClick={() => setZoom((z) => Math.max(0.25, z - 0.25))}
                className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-md"
                title="Zoom out"
              ><ZoomOut size={16} /></button>
              <span className="text-xs font-mono text-slate-600 w-12 text-center" title="Mouse-wheel over the preview to zoom">{Math.round(zoom * 100)}%</span>
              <button
                data-testid="preview-zoom-in"
                onClick={() => setZoom((z) => Math.min(4, z + 0.25))}
                className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-md"
                title="Zoom in"
              ><ZoomIn size={16} /></button>
              <button
                data-testid="preview-rotate"
                onClick={() => setRotation((r) => (r + 90) % 360)}
                className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-md"
                title="Rotate"
              ><RotateCw size={16} /></button>
              <div className="w-px h-5 bg-slate-200" />
            </>
          )}
          <button
            data-testid="preview-popout-btn"
            onClick={popOut}
            disabled={!blobUrl}
            className="text-xs bg-sky-50 border border-sky-200 text-sky-700 hover:bg-sky-100 rounded-md px-2.5 py-1.5 inline-flex items-center gap-1.5 font-semibold disabled:opacity-50"
            title="Open in separate window — drag to your second screen for reference"
          >
            <ExternalLink size={13} /> Pop out
          </button>
          <button
            data-testid="preview-download-btn"
            onClick={download}
            disabled={!blobUrl}
            className="text-xs bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-md px-2.5 py-1.5 inline-flex items-center gap-1.5 font-semibold disabled:opacity-50"
          >
            <Download size={13} /> Download
          </button>
          <button
            data-testid="preview-close-btn"
            onClick={onClose}
            className="p-1.5 text-slate-500 hover:text-rose-700 hover:bg-rose-50 rounded-md"
            title="Close"
          ><X size={18} /></button>
        </div>

        {/* Body */}
        <div
          ref={bodyRef}
          className="flex-1 overflow-auto bg-slate-100 flex items-center justify-center"
        >
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 p-8">
              <Loader2 size={16} className="animate-spin" /> Loading preview…
            </div>
          ) : err ? (
            <div data-testid="preview-error" className="text-sm text-rose-700 p-8 text-center">
              <AlertTriangle size={20} className="mx-auto mb-2" />
              <div className="font-semibold">Cannot preview this file</div>
              <div className="text-xs text-rose-500 mt-1">{err}</div>
            </div>
          ) : isImage ? (
            <img
              data-testid="preview-image"
              src={blobUrl}
              alt={attachment.original_filename}
              style={{
                transform: `scale(${zoom}) rotate(${rotation}deg)`,
                transformOrigin: 'center center',
                transition: 'transform 200ms ease-out',
              }}
              className="max-w-full max-h-full object-contain p-4"
            />
          ) : isPdf ? (
            <object
              data-testid="preview-pdf"
              data={blobUrl}
              type="application/pdf"
              className="w-full h-[80vh]"
            >
              <div className="p-8 text-center">
                <p className="text-sm text-slate-600 mb-2">Your browser can't render PDFs inline.</p>
                <button onClick={popOut} className="text-sm text-sky-700 underline">Open in new window</button>
              </div>
            </object>
          ) : isText ? (
            <BlobTextView blobUrl={blobUrl} testid="preview-text" />
          ) : (
            <div className="text-sm text-slate-600 p-8 text-center">
              <Maximize2 size={20} className="mx-auto mb-2 text-slate-400" />
              <div className="font-semibold">Preview not available for {mime || 'this file type'}</div>
              <div className="text-xs text-slate-500 mt-1">Use "Pop out" or "Download" to view in another app.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Small helper to render plain-text blobs inline. */
function BlobTextView({ blobUrl, testid }) {
  const [text, setText] = useState('');
  useEffect(() => {
    if (!blobUrl) return;
    fetch(blobUrl)
      .then((r) => r.text())
      .then(setText)
      .catch(() => setText('(unable to load text content)'));
  }, [blobUrl]);
  return (
    <pre data-testid={testid} className="text-xs font-mono text-slate-800 bg-white p-4 m-4 rounded-md border border-slate-200 max-h-[75vh] overflow-auto whitespace-pre-wrap w-full max-w-4xl">
      {text}
    </pre>
  );
}
